export type StructuredData = Record<string, unknown>;

export const SITE_URL = 'https://goproblems.com';
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const ORGANIZATION_LOGO = `${SITE_URL}/build/images/logo.svg`;

type BreadcrumbItem = {
    name: string;
    path: string;
};

type PersonSchemaInput = {
    name: string;
    image?: string;
    description?: string;
};

type ArticleSchemaInput = {
    slug: string;
    headline: string;
    description: string;
    authorName: string;
    datePublished?: string | null;
    image?: string;
    aboutPage?: boolean;
};

type ProblemSchemaInput = {
    id: number;
    name: string;
    description: string;
    image?: string;
    dateCreated?: string | null;
    authorName: string;
};

type CollectionProblem = {
    id: number;
    imageUrl?: string;
};

type CollectionSchemaInput = {
    id: number;
    name: string;
    description: string;
    authorName: string;
    dateCreated?: string | null;
    numberOfProblems: number;
    problems: CollectionProblem[];
};

export const absoluteSchemaUrl = (path: string): string =>
    new URL(path, `${SITE_URL}/`).toString();

const personUrl = (name: string): string =>
    `${SITE_URL}/users/${encodeURIComponent(name)}`;

const organizationSchema = (): StructuredData => ({
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: 'Go Problems',
    url: `${SITE_URL}/`,
    logo: {
        '@type': 'ImageObject',
        url: ORGANIZATION_LOGO,
        width: 1417,
        height: 270,
    },
});

export const createWebsiteSchema = (): StructuredData => ({
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'WebSite',
            '@id': `${SITE_URL}/#website`,
            url: `${SITE_URL}/`,
            name: 'Go Problems',
            description:
                'Solve interactive Go problems, practise tsumego, explore problem collections, track your progress, and improve your Baduk or Weiqi reading skills.',
            publisher: {'@id': ORGANIZATION_ID},
        },
        organizationSchema(),
    ],
});

export const createBreadcrumbSchema = (
    items: BreadcrumbItem[]
): StructuredData => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${absoluteSchemaUrl(items.at(-1)?.path ?? '/')}#breadcrumb`,
    itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: absoluteSchemaUrl(item.path),
    })),
});

export const createArticleSchema = ({
    slug,
    headline,
    description,
    authorName,
    datePublished,
    image,
    aboutPage = false,
}: ArticleSchemaInput): StructuredData => {
    const url = `${SITE_URL}/article/${slug}`;

    return {
        '@context': 'https://schema.org',
        '@type': aboutPage ? 'AboutPage' : 'Article',
        '@id': `${url}#${aboutPage ? 'about-page' : 'article'}`,
        url,
        headline,
        name: headline,
        description,
        ...(datePublished ? {datePublished} : {}),
        author: {
            '@type': 'Person',
            name: authorName,
            url: personUrl(authorName),
        },
        publisher: organizationSchema(),
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': url,
            url,
        },
        ...(image ? {image} : {}),
    };
};

export const createProfileSchema = ({
    name,
    image,
    description,
    dateCreated,
}: PersonSchemaInput & {dateCreated?: string | null}): StructuredData => {
    const url = personUrl(name);

    return {
        '@context': 'https://schema.org',
        '@type': 'ProfilePage',
        '@id': `${url}#profile-page`,
        url,
        ...(dateCreated ? {dateCreated} : {}),
        mainEntity: {
            '@type': 'Person',
            '@id': `${url}#person`,
            name,
            url,
            ...(image ? {image} : {}),
            ...(description ? {description} : {}),
            knowsAbout: ['Go', 'Baduk', 'Weiqi'],
        },
    };
};

export const createProblemSchema = ({
    id,
    name,
    description,
    image,
    dateCreated,
    authorName,
}: ProblemSchemaInput): StructuredData => {
    const url = `${SITE_URL}/problems/${id}`;

    return {
        '@context': 'https://schema.org',
        '@type': 'LearningResource',
        '@id': `${url}#learning-resource`,
        name,
        description,
        url,
        ...(image ? {image} : {}),
        ...(dateCreated ? {dateCreated} : {}),
        author: {
            '@type': 'Person',
            name: authorName,
            url: personUrl(authorName),
        },
        learningResourceType: 'Interactive Go problem',
        about: ['Go', 'Baduk', 'Weiqi', 'Tsumego'],
        isAccessibleForFree: true,
    };
};

export const createCollectionSchema = ({
    id,
    name,
    description,
    authorName,
    dateCreated,
    numberOfProblems,
    problems,
}: CollectionSchemaInput): StructuredData => {
    const url = `${SITE_URL}/collections/${id}`;

    return {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        '@id': `${url}#collection-page`,
        url,
        name,
        description,
        ...(dateCreated ? {dateCreated} : {}),
        author: {
            '@type': 'Person',
            name: authorName,
            url: personUrl(authorName),
        },
        mainEntity: {
            '@type': 'ItemList',
            '@id': `${url}#problem-list`,
            name,
            numberOfItems: numberOfProblems,
            itemListElement: problems.map((problem, index) => {
                const problemUrl = `${SITE_URL}/problems/${problem.id}`;

                return {
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                        '@type': 'LearningResource',
                        '@id': `${problemUrl}#learning-resource`,
                        url: problemUrl,
                        name: `Go Problem #${problem.id}`,
                        ...(problem.imageUrl
                            ? {image: absoluteSchemaUrl(problem.imageUrl)}
                            : {}),
                    },
                };
            }),
        },
    };
};
